import z from 'zod';

/**
 * 시딩 신청/수락 공용 request 하위 스키마.
 * `applyRecruitmentCampaign` / `acceptSeedingProposal` 이 동일한 body shape(선택 옵션 +
 * 콘텐츠 유형 + 배송지 + 메시지)을 사용해 한 곳에 모은다.
 */

export const SeedingMutationContentTypeSchema = z
  .enum(['IMAGE', 'SHORT_FORM', 'LONG_FORM', 'TEXT', 'BLOG_POST'])
  .describe('SeedingMutationContentType');

export const SeedingMutationSnsChannelSchema = z
  .enum(['INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'NAVER_BLOG', 'THREADS', 'SNAP'])
  .describe('SeedingMutationSnsChannel');

/** 선택한 상품 옵션 — goodsNo + goodsOptionNo(=SKU) + 추가옵션(MUSINSA 한정). */
export const SeedingProposalSelectedOptionSchema = z
  .object({
    goodsNo: z.number().int(),
    // 단품(옵션 없는 29CM 상품)은 SKU 가 없어 미전송/null. (SID-4296 0612 계약)
    goodsOptionNo: z.number().int().nullish(),
    extraOptionNos: z.array(z.number().int()),
  })
  .describe('SeedingProposalSelectedOption');

export type SeedingProposalSelectedOption = z.infer<typeof SeedingProposalSelectedOptionSchema>;

/** 배송지 — MUSINSA 는 `deliveryAddressId`(등록 주소 선택) 권장, 29CM 은 inline 입력. */
export const SeedingProposalDeliveryAddressSchema = z
  .object({
    deliveryAddressId: z.number().int().nullable().optional(),
    addressName: z.string().nullable().optional(),
    name: z.string(),
    phone: z.string(),
    zipcode: z.string(),
    address1: z.string(),
    address2: z.string().nullable().optional(),
    memo: z.string().nullable().optional(),
  })
  .describe('SeedingProposalDeliveryAddress');

export type SeedingProposalDeliveryAddress = z.infer<typeof SeedingProposalDeliveryAddressSchema>;

/**
 * 캠페인 참여 필수 동의 항목 코드 (curator-api 0609).
 * 전송 시 아래 전체와 정확히 일치해야 동의가 저장된다(누락/오타/구버전 혼입 → BE 400).
 */
export const SeedingConsentCodeSchema = z
  .enum(['CAMPAIGN_PARTICIPATION_V1', 'CONTENT_COPYRIGHT_V1', 'AD_GUIDELINE_V1', 'TAX_V1'])
  .describe('SeedingConsentCode');

export type SeedingConsentCode = z.infer<typeof SeedingConsentCodeSchema>;

/** 신청/수락 시 전송해야 하는 필수 동의 코드 전체(순서 무관, 전 항목 필수). */
export const SEEDING_REQUIRED_CONSENT_CODES: SeedingConsentCode[] = [
  'CAMPAIGN_PARTICIPATION_V1',
  'CONTENT_COPYRIGHT_V1',
  'AD_GUIDELINE_V1',
  'TAX_V1',
];

/** 신청/수락 공용 body. */
export const SeedingApplyAcceptBodySchema = z
  .object({
    selectedOptions: z.array(SeedingProposalSelectedOptionSchema),
    contentType: SeedingMutationContentTypeSchema,
    deliveryAddress: SeedingProposalDeliveryAddressSchema,
    messageToBrand: z.string().nullable().optional(),
    // 동의 항목 — 미전송(전환기)이면 참여는 정상, 동의는 저장되지 않음.
    agreedConsents: z.array(SeedingConsentCodeSchema).nullable().optional(),
    // 동의를 진행한 화면 URL (감사용, 최대 512자).
    agreementPath: z.string().max(512).nullable().optional(),
  })
  .describe('SeedingApplyAcceptBody');

export type SeedingApplyAcceptBody = z.infer<typeof SeedingApplyAcceptBodySchema>;
